import { cn } from '@/shared/utils/cn';
import { AvatarColor, AvatarTheme, AvatarSize } from '../types';
import {
  SOLID_COLORS,
  SOFT_COLORS,
  SOFT_LETTER_COLORS,
  AVATAR_SIZES,
  STROKE_WIDTH,
  PEOPLE_STROKE_COLOR,
  SOFT_STROKE_COLORS,
  SOLID_STROKE_COLORS,
  PILL_SHAPE_CLASS,
  ROUNDED_SHAPE_CLASS,
  ROUNDED_SHAPE_BY_SIZE,
  MAX_TEXT_LENGTH,
  DISABLED_CLASS,
  DISABLED_GRAYSCALE_FILTER,
} from '../constants';

/**
 * ============================================
 * AVATAR UTILS - ATOMIC DESIGN
 * ============================================
 * Funciones auxiliares para generar clases y procesar datos
 */

/**
 * Obtiene las clases de color según el tema
 *
 * @param color - Color del avatar
 * @param theme - Tema de estilo (solid o soft)
 * @param avatarType - Tipo de avatar (para personalizar letter/icon)
 * @param disabled - Si está deshabilitado
 * @returns Clases de Tailwind para el color
 *
 * @example
 * getColorClasses('blue', 'solid')
 * // => 'ui-bg-blue-600 ui-text-white'
 */
export const getColorClasses = (
  color: AvatarColor,
  theme: AvatarTheme,
  avatarType?: 'icon' | 'letter',
  disabled: boolean = false,
): string => {
  if (disabled) {
    if (avatarType === 'letter') {
      return SOFT_LETTER_COLORS[color];
    }

    return SOFT_COLORS[color];
  }

  if (theme === 'solid') {
    return SOLID_COLORS[color];
  }

  if (avatarType === 'letter') {
    return SOFT_LETTER_COLORS[color];
  }

  return SOFT_COLORS[color];
};

/**
 * Obtiene las clases de tamaño del avatar
 *
 * @param size - Tamaño del avatar
 * @returns Clases de Tailwind para el tamaño
 *
 * @example
 * getSizeClasses('md')
 * // => 'ui-w-40 ui-h-40 ui-text-[12px]'
 */
export const getSizeClasses = (size: AvatarSize): string => {
  return AVATAR_SIZES[size];
};

/**
 * Obtiene las clases de borde (stroke) según el tipo de avatar
 *
 * @param stroke - Si debe mostrar borde
 * @param avatarType - Tipo de avatar ('people', 'icon', 'letter')
 * @param color - Color del avatar (solo para icon/letter)
 * @param theme - Tema (solo para icon/letter)
 * @returns Clases de Tailwind para el borde
 *
 * @example
 * // People avatar - borde gris fijo
 * getStrokeClasses(true, 'people')
 * // => 'ui-border-2 ui-border-default'
 *
 * @example
 * // Icon/Letter con soft - borde del color del background soft
 * getStrokeClasses(true, 'icon', 'blue', 'soft')
 * // => 'ui-border-2 ui-border-surface-all-colors-blue'
 */
export const getStrokeClasses = (
  stroke: boolean,
  avatarType?: 'people' | 'icon' | 'letter',
  color?: AvatarColor,
  theme?: AvatarTheme,
  disabled: boolean = false,
): string => {
  if (!stroke) return '';

  // People avatar siempre tiene borde gris
  if (avatarType === 'people') {
    return cn(STROKE_WIDTH, PEOPLE_STROKE_COLOR, 'ui-border-default');
  }

  if ((avatarType === 'icon' || avatarType === 'letter') && color) {
    if (disabled) {
      return cn(STROKE_WIDTH, SOFT_STROKE_COLORS[color]);
    }

    const strokeColor = theme === 'soft' ? SOFT_STROKE_COLORS[color] : SOLID_STROKE_COLORS[color];

    return cn(STROKE_WIDTH, strokeColor, 'ui-border-default');
  }

  return cn(STROKE_WIDTH, PEOPLE_STROKE_COLOR, 'ui-border-default');
};

/**
 * Obtiene las clases de forma (pill)
 *
 * @param pill - true = circular, false = redondeado
 * @param size - Tamaño del avatar (para ajustar radios pequeños)
 * @returns Clases de Tailwind para la forma
 *
 * @example
 * getShapeClasses(true)
 * // => 'ui-rounded-control-pill'
 */
export const getShapeClasses = (pill: boolean, size?: AvatarSize): string => {
  if (pill) {
    return PILL_SHAPE_CLASS;
  }

  if (!size) {
    return ROUNDED_SHAPE_CLASS;
  }

  return ROUNDED_SHAPE_BY_SIZE[size] ?? ROUNDED_SHAPE_CLASS;
};

/**
 * Genera las clases base del contenedor del avatar
 *
 * Combina todas las clases necesarias para el avatar:
 * - Layout (flex, centrado)
 * - Tamaño
 * - Borde (si aplica)
 * - Forma (circular o redondeado)
 * - Estado disabled
 * - Clases personalizadas
 *
 * @param size - Tamaño del avatar
 * @param stroke - Si debe mostrar borde
 * @param pill - Forma del avatar
 * @param disabled - Si está deshabilitado
 * @param grayscaleWhenDisabled - Si aplica filtro grayscale cuando está disabled
 * @param className - Clases CSS adicionales
 * @param avatarType - Tipo de avatar (para stroke)
 * @param color - Color del avatar (para stroke)
 * @param theme - Tema (para stroke)
 * @returns Clases combinadas para el avatar
 */
export const getBaseClasses = (
  size: AvatarSize,
  stroke: boolean,
  pill: boolean,
  disabled: boolean,
  grayscaleWhenDisabled: boolean,
  className?: string,
  avatarType?: 'people' | 'icon' | 'letter',
  color?: AvatarColor,
  theme?: AvatarTheme,
): string => {
  return cn(
    // Layout base
    'ui-flex ui-items-center ui-justify-center',
    // Tipografía
    'ui-font-semibold ui-leading-none ui-select-none',
    // Tamaño
    getSizeClasses(size),
    // Borde
    getStrokeClasses(stroke, avatarType, color, theme, disabled),
    // Forma (con overflow-hidden para que funcione correctamente en imágenes)
    getShapeClasses(pill, size),
    !pill && 'ui-overflow-hidden', // Overflow hidden solo cuando no es pill para que el border-radius funcione
    // Estado disabled
    disabled && DISABLED_CLASS,
    disabled && grayscaleWhenDisabled && DISABLED_GRAYSCALE_FILTER,
    // Clases personalizadas
    className,
  );
};

/**
 * Trunca el texto a un máximo de caracteres
 *
 * Útil para avatares tipo 'letter' que muestran iniciales,
 * números de contador (99+), o indicadores (+1)
 *
 * @param text - Texto a truncar
 * @param maxLength - Longitud máxima (default: 3)
 * @returns Texto truncado en mayúsculas
 *
 * @example
 * truncateText('hello', 3) // => 'HEL'
 * truncateText('99+', 3)   // => '99+'
 * truncateText('abc')      // => 'ABC'
 */
export const truncateText = (text: string, maxLength: number = MAX_TEXT_LENGTH): string => {
  return text.slice(0, maxLength).toUpperCase();
};

/**
 * Valida si una URL es válida
 *
 * Usado para validar URLs de imágenes en avatares tipo 'people'
 *
 * @param url - URL a validar
 * @returns true si es una URL válida
 *
 * @example
 * isValidUrl('https://example.com/image.jpg') // => true
 * isValidUrl('not-a-url')                      // => false
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
